import React, { forwardRef } from 'react';
import { withRouter } from 'react-router';
import { Paper, withStyles, Button } from '@material-ui/core';
import { IFieldsProps, IWidgetConfigurationContentHandle } from './WidgetConfigurationContent.properties';
import styles from './WidgetConfigurationContent.styles';
import DisplayFieldsGrid from './DisplayFieldsGrid';
import FieldsGrid from './FieldsGrid';
import { TextField } from '@markinson/uicomponents-v2';
import { useSetDashboardWidgetFieldListQueryData } from 'api/dashboardMaintenanceEnquiry/dashboardMaintenanceEnquiry';
import { WIDGET_TYPES } from 'components/common/Dashboard/Dashboard.constants';

const WidgetConfigurationContent = (props: IFieldsProps, ref: React.Ref<IWidgetConfigurationContentHandle>) => {
    const { classes, widgetId, selectedDashboardType, selectedWidget } = props;
    const [widgetTitle, setWidgetTitle] = React.useState<string>(selectedWidget?.WidgetTitle || '');
    const [maxDisplayRow, setMaxDisplayRow] = React.useState<string>(selectedWidget?.MaxDisplayRow || '');
    const firstButtonRef = React.useRef<HTMLButtonElement>(null);
    const lastButtonRef = React.useRef<HTMLButtonElement>(null);
    const fieldsGridInnerRef = React.useRef(null);
    const displayFieldsGridInnerRef = React.useRef(null);
    const setDashboardWidgetFieldListQueryData = useSetDashboardWidgetFieldListQueryData();
    const ELEMENT2 = 2;

    const handleAddDisplayFields = async () => {
        const selectedItems = fieldsGridInnerRef?.current?.getSelectedRowsData?.() || [];
        const existingItems = displayFieldsGridInnerRef.current?.getData() || [];

        const existingIds = new Set(existingItems.map((item) => item.WidgetFieldId));
        const uniqueNewItems = selectedItems.filter((item) => !existingIds.has(item.WidgetFieldId));

        if (uniqueNewItems.length === 0) {
            fieldsGridInnerRef?.current?.deselectAll?.();

            return;
        }

        const lastSequence = existingItems.length > 0
            ? Math.max(...existingItems.map((item) => item.Sequence || 0))
            : 0;

        const selectedItemsWithSequence = uniqueNewItems.map((item, index) => ({
            ...item,
            Sequence: lastSequence + index + 1,
        }));

        setDashboardWidgetFieldListQueryData(Number(widgetId), {
            DashboardWidgetFields: [...existingItems, ...selectedItemsWithSequence],
        });

        setTimeout(() => { fieldsGridInnerRef?.current?.deselectAll?.(); }, 0);
    };

    React.useImperativeHandle(ref, () => ({
        getDisplayFieldsGridData: () => {
            if (displayFieldsGridInnerRef.current) {

                return displayFieldsGridInnerRef.current?.getData();
            }

            return [];
        },
        getTextFieldsData: () => {

            return {
                WidgetTitle: widgetTitle,
                MaxDisplayRow: maxDisplayRow
            };
        }
    }));

    return (<>
        <div className={classes.fieldsContainer}>
            <div className={classes.leftContainer}>
                <Paper className={classes.paperLeftContainer}>
                    <Paper className={classes.paperLeft} elevation={0}>
                    <TextField
                        label={'Widget title'}
                        name={'Title'}
                        size='large'
                        value={widgetTitle}
                        onChange={(value) => { setWidgetTitle(value); }}
                        className={classes.widgetTitleTextField}
                    />
                    </Paper>
                    <Paper className={classes.fieldsLeftContainer} elevation={0}>
                        <Paper className={classes.fieldContainerLabel} elevation={0}>Fields</Paper>
                        <FieldsGrid innerRef={fieldsGridInnerRef} selectedDashboardType={selectedDashboardType} widgetId={selectedWidget?.WidgetId} />
                    </Paper>
                    <div className={classes.buttonsContainer}>
                        {
                            [
                                {
                                    id: 0,
                                    label: 'Add fields',
                                    isDefault: true,
                                    listener: handleAddDisplayFields
                                },
                            ].map((button) => {
                                return (
                                    <Button
                                        key={button.id}
                                        color='primary'
                                        onClick={() => { button.listener(); }}
                                        // disabled={button.disabled}
                                        autoFocus={button.isDefault}
                                        className={(button.isDefault) ? classes.active : classes.action}
                                        buttonRef={(Ref) => {
                                            if (button.id === 0) {
                                                firstButtonRef.current = Ref;
                                            }
                                            if (button.id === ELEMENT2) {
                                                lastButtonRef.current = Ref;
                                            }
                                        }}
                                    >
                                        {button.label}
                                    </Button>
                                );
                            })
                        }
                    </div>
                </Paper>
            </div>
            <div className={classes.rightContainer}>
                <Paper className={classes.outputFieldsContainer}>
                    {
                        WIDGET_TYPES[selectedWidget?.WidgetId]?.widget === 'grid' && (
                            <TextField
                                label={'Maximum rows'}
                                name={'MaxDisplayRow'}
                                size='large'
                                value={maxDisplayRow}
                                onChange={(value) => { setMaxDisplayRow(value); }}
                            />
                        )
                    }
                    <div style={{ minHeight: WIDGET_TYPES[selectedWidget?.WidgetId]?.widget === 'grid' ? '305px' : '400px' }}>
                        <Paper className={classes.containerLabel} elevation={0}>{WIDGET_TYPES[selectedWidget?.WidgetId]?.widget === 'grid' ? 'Grid columns' : WIDGET_TYPES[selectedWidget?.WidgetId]?.widget === 'fields' ? 'Display fields' : WIDGET_TYPES[selectedWidget?.WidgetId]?.widget === 'alert' ? 'Widget alerts' : ''}</Paper>
                        <DisplayFieldsGrid innerRef={displayFieldsGridInnerRef} widgetId={widgetId} />
                    </div>
                </Paper>
            </div>
        </div>
    </>);
};

export default withRouter(withStyles(styles, { index: 1 })(React.memo(forwardRef(WidgetConfigurationContent))));
